import React, { useEffect, useState, useContext } from "react";
import { Page, FormSectionCard } from "../layout/page";
import { Calendar } from "@mantine/dates";
import { useTranslation } from "react-i18next";
import { LanguageContext } from "../context/language";
import {
  Group,
  Indicator,
  Table,
  Text,
  Box,
  SegmentedControl,
  ScrollArea,
  Button,
  UnstyledButton,
  Image,
  Avatar,
  ActionIcon,
  Tooltip,
  Badge,
} from "@mantine/core";
import moment from "moment";
import _ from "lodash";

import { useCellRender, openWhatsapp } from "../hooks/useCellRender";
import { formatPhoneDisplay } from "../helpers/phoneUtils";
import {
  IconBrandWhatsapp,
  IconCalendar,
  IconClock,
  IconPhone,
  IconExternalLink,
  IconMap,
  IconDownload,
} from "@tabler/icons";
import { useServerApi } from "../hooks/userServerApi";
import { useAuthUser } from "react-auth-kit";
import { useLocation } from "react-router-dom";
import { useTabNavigate } from "../hooks/useTabNavigate";
import { openEntityEditTab } from "../utils/openEntityEditTab";
import { resolveEntityPath } from "../utils/resolveEntityPath";
import { ImageThumbList } from "./clientPaymentList";
import { openFileUrl } from "../utils/openReport";

export const getEventColor = (type) => {
  switch (type) {
    case "companyEvent":
      return "red";
    case "inTraining":
      return "yellow";
    case "meeting":
      return "orange";
    case "birthday":
      return "cyan";
    case "vacation":
      return "purple";
    default:
      return "blue";
  }
};

const EventCalendar = ({ events, onMonthChange }) => {
  const eventDates = events?.map((e) => moment(e.date)) ?? [];
  const { t } = useTranslation();

  const isEventDate = (date) => {
    if (moment().isSame(date, "date")) return true;
    for (const d of eventDates) {
      if (d?.isSame(date, "date")) return true;
    }
    if (eventDates.include(date.get)) return false;
  };

  return (
    <Box p="md" sx={{ width: "100%" }}>
      <Calendar
        w={"100%"}
        onMonthChange={onMonthChange}
        onChange={() => {}}
        renderDay={(date) => {
          const day = date.getDate();
          const event = events?.find((e) =>
            moment(e.date).isSame(date.toISOString(), "date"),
          );
          const isToday = moment().isSame(date.toISOString(), "date");
          return (
            <>
              {event && (
                <Indicator size={6} color={getEventColor(event.type)}>
                  <div>{day}</div>
                </Indicator>
              )}
              {!event && isToday && (
                <Indicator size={6} color="teal">
                  <div>{day}</div>
                </Indicator>
              )}
              {!event && !isToday && <div>{day}</div>}
            </>
          );
        }}
      />
      <Box mt={"xl"}>
        <Group>
          <Indicator size={6} color="teal" />
          <Text size="xs">{t("Today")}</Text>
        </Group>
        <Group noWrap>
          <Indicator size={6} color="red" />
          <Text size="xs">{t("Company Event")}</Text>
        </Group>
        <Group>
          <Indicator size={6} color="yellow" />
          <Text size="xs">{t("Internal Training")}</Text>
        </Group>
        <Group>
          <Indicator size={6} color="orange" />
          <Text size="xs">{t("Meeting")}</Text>
        </Group>
        <Group>
          <Indicator size={6} color="cyan" />
          <Text size="xs">{t("Client Birthday")}</Text>
        </Group>
        <Group>
          <Indicator size={6} color="purple" />
          <Text size="xs">{t("Vacation")}</Text>
        </Group>
      </Box>
    </Box>
  );
};
const EventSection = ({
  title,
  txtSize = "xs",
  headerSize = "xs",
  iconSize = 12,
  ...props
}) => {
  const [cellRender] = useCellRender();
  const [events, setEvents] = useState([]);
  const [clients, setClients] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [incentives, setIncentives] = useState([]);
  const [pendingRenewals, setPendingRenewals] = useState([]);
  const [downloadingRenewals, setDownloadingRenewals] = useState(false);
  const [activeTab, setActiveTab] = useState("events");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [api] = useServerApi();
  const { t } = useTranslation();

  const auth = useAuthUser();
  const location = useLocation();
  const tabNav = useTabNavigate();

  const canOpenEntity = (entity) => {
    const userRole = auth()?.userRole;
    return Boolean(resolveEntityPath(entity, userRole, location.pathname));
  };

  const openLinkedEntity = (entity, id, label) => {
    const userRole = auth()?.userRole;
    if (!id || !userRole) return;
    openEntityEditTab(tabNav, {
      entity,
      id,
      label,
      userRole,
      currentPathname: location.pathname,
    });
  };

  const fetchEvent = async () => {
    try {
      const e = [...((await api.CalendarEvent.getByMonth(currentDate)) ?? [])];
      const userId = auth()?.id;
      if (userId) {
        const endOfMonthDate = moment(currentDate).endOf("month").toISOString();
        const result = await api.Client.getBirthdays(userId, endOfMonthDate);
        const clients = result?.data?.clients;
        if (!_.isEmpty(clients)) {
          clients.forEach((c) =>
            e.push({
              date: moment(c.birthday),
              title: `${t("Client Birthday")} ${moment
                .localeData()
                .ordinal(c.age)}`,
              type: "birthday",
              venues: (
                <Group noWrap position="apart">
                  <a
                    href={`/consultant/client?id=${c._id}`}
                    target="_blank"
                    style={{ textDecoration: "none" }}
                  >
                    {`${c.name} ${c.cname}`}
                  </a>
                  <UnstyledButton onClick={() => openWhatsapp(c.phone)}>
                    <Group spacing={3} noWrap>
                      <IconBrandWhatsapp size={10}></IconBrandWhatsapp>
                      <Text size="xs" color={"dimmed"}>
                        {formatPhoneDisplay(c.phone)}
                      </Text>
                    </Group>
                  </UnstyledButton>
                </Group>
              ),
            }),
          );
        }
      }

      setEvents(e);

      const promos = await api.ClientPromotion.getByMonth(currentDate);
      setPromotions(promos || []);

      const incents = await api.ConsultantIncentive.getByMonth(currentDate);
      setIncentives(incents || []);

      const pRenewals = await api.Renewal.getPendingRenewalsByUser(
        moment(currentDate).format("YYYY-MM"),
      );
      setPendingRenewals(pRenewals?.data?.debitNotes || []);
    } catch (error) {
      console.log(error);
      setEvents([]);
      setPromotions([]);
      setIncentives([]);
      setPendingRenewals([]);
    }
  };

  const downloadPendingRenewals = async () => {
    try {
      setDownloadingRenewals(true);
      const result = await api.Renewal.getPendingRenewalReportByUser(
        moment(currentDate).format("YYYY-MM"),
      );
      if (result?.data?.url) {
        await openFileUrl(result.data.url);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setDownloadingRenewals(false);
    }
  };

  useEffect(() => {
    fetchEvent();
  }, [currentDate]);

  const calendarIcon = (
    <IconCalendar size={iconSize} style={{ flexShrink: 0 }} />
  );

  const eventRows = events?.map((ev) => (
    <tr key={ev._id}>
      <td>
        <Group noWrap align="flex-start">
          {calendarIcon}
          <Text size={txtSize}>
            {ev.endDate
              ? `${moment(ev.date).format("LL")} ~ ${moment(ev.endDate).format(
                  "LL",
                )}`
              : moment(ev.date).format("LL")}
          </Text>
        </Group>
      </td>

      <td>
        <Group noWrap>
          <Indicator size={6} color={getEventColor(ev.type)} zIndex={1} />
        </Group>
      </td>

      <td>
        <Group noWrap>
          <Text size={txtSize} lineClamp={1}>
            {ev.title}
          </Text>
        </Group>
        <Group noWrap>
          {/* <IconMap size={iconSize}></IconMap> */}
          <Text size={"xs"} color="dimmed">
            {ev.venues}
          </Text>
        </Group>
      </td>
      <td>
        <Group noWrap>
          {ev.fromTime && <IconClock size={iconSize}></IconClock>}
          <Text size={txtSize}>
            {ev.fromTime ? moment(ev.fromTime).format("HH:mm") : ""}
            {ev.toTime ? ` - ${moment(ev.toTime).format("HH:mm")}` : ""}
          </Text>
        </Group>
      </td>

      {/* <td>{ev.description}</td> */}
    </tr>
  ));

  const promotionRows = promotions?.map((promo) => (
    <tr key={promo._id}>
      <td>
        <Group noWrap align="flex-start">
          {calendarIcon}
          <Text size={txtSize}>
            {promo.submitDateRange?.start && promo.submitDateRange?.end
              ? `${moment(promo.submitDateRange.start).format("LL")} ~ ${moment(
                  promo.submitDateRange.end,
                ).format("LL")}`
              : "-"}
          </Text>
        </Group>
      </td>
      <td>
        <Group noWrap spacing="xs">
          {promo.provider?.image ? (
            <Avatar src={promo.provider.image} size={24} radius="xl" />
          ) : (
            <Avatar size={24} radius="xl">
              {promo.provider?.shortName?.charAt(0) || "P"}
            </Avatar>
          )}
          <Text size={txtSize}>
            {promo.provider?.shortName || promo.provider?.name || "-"}
          </Text>
        </Group>
      </td>
      <td>
        <Group noWrap spacing="xs">
          {promo.url ? (
            <a
              href={promo.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none" }}
            >
              <Text size={txtSize} color="blue" lineClamp={1}>
                {promo.title}
              </Text>
            </a>
          ) : (
            <Text size={txtSize} lineClamp={1}>
              {promo.title}
            </Text>
          )}
          <ImageThumbList files={promo.files} />
        </Group>
        {promo.description && (
          <Text size="xs" color="dimmed" lineClamp={1}>
            {promo.description}
          </Text>
        )}
      </td>
      <td>
        <Text size={txtSize}>
          {promo.inForceBefore ? moment(promo.inForceBefore).format("LL") : "-"}
        </Text>
      </td>
    </tr>
  ));

  const incentiveRows = incentives?.map((incentive) => (
    <tr key={incentive._id}>
      <td>
        <Group noWrap align="flex-start">
          {calendarIcon}
          <Text size={txtSize}>
            {incentive.submitDateRange?.start && incentive.submitDateRange?.end
              ? `${moment(incentive.submitDateRange.start).format(
                  "LL",
                )} ~ ${moment(incentive.submitDateRange.end).format("LL")}`
              : "-"}
          </Text>
        </Group>
      </td>
      <td>
        <Group noWrap spacing="xs">
          {incentive.provider?.image ? (
            <Avatar src={incentive.provider.image} size={24} radius="xl" />
          ) : (
            <Avatar size={24} radius="xl">
              {incentive.provider?.shortName?.charAt(0) || "I"}
            </Avatar>
          )}
          <Text size={txtSize}>
            {incentive.provider?.shortName || incentive.provider?.name || "-"}
          </Text>
        </Group>
      </td>
      <td>
        <Group noWrap spacing="xs">
          {incentive.url ? (
            <a
              href={incentive.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none" }}
            >
              <Text size={txtSize} color="blue" lineClamp={1}>
                {incentive.title}
              </Text>
            </a>
          ) : (
            <Text size={txtSize} lineClamp={1}>
              {incentive.title}
            </Text>
          )}
          <ImageThumbList files={incentive.files} />
        </Group>
        {incentive.description && (
          <Text size="xs" color="dimmed" lineClamp={1}>
            {incentive.description}
          </Text>
        )}
      </td>
      <td>
        <Text size={txtSize}>
          {incentive.inForceBefore
            ? moment(incentive.inForceBefore).format("LL")
            : "-"}
        </Text>
      </td>
    </tr>
  ));

  return (
    <FormSectionCard title={title} {...props}>
      <Group align="flex-start" spacing="md" noWrap sx={{ width: "100%" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SegmentedControl
            value={activeTab}
            onChange={setActiveTab}
            fullWidth
            withItemsBorders={false}
            data={[
              { label: t("Events"), value: "events" },
              { label: t("Client Promotion"), value: "promotions" },
              { label: t("Consultant Incentive"), value: "incentives" },
              { label: t("GI Renewals"), value: "pendingRenewals" },
            ]}
            mb="md"
          />

          {activeTab === "events" && (
            <ScrollArea h={"400px"} type="auto">
              <Table>
                <thead>
                  <tr>
                    <th>
                      <Text size={headerSize}>{t("Date")} </Text>
                    </th>
                    <th>
                      <Text size={headerSize}>{t("Type")} </Text>
                    </th>
                    <th>
                      <Text size={headerSize}>{t("Event")}</Text>
                    </th>
                    <th>
                      <Text size={headerSize}>{t("Duration")} </Text>
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>{eventRows}</tbody>
              </Table>
            </ScrollArea>
          )}

          {activeTab === "promotions" && (
            <ScrollArea h={"400px"} type="auto">
              <Table>
                <thead>
                  <tr>
                    <th>
                      <Text size={headerSize}>{t("Date Range")} </Text>
                    </th>
                    <th>
                      <Text size={headerSize}>{t("Insurer")} </Text>
                    </th>
                    <th>
                      <Text size={headerSize}>{t("Title")}</Text>
                    </th>
                    <th>
                      <Text size={headerSize}>{t("In Force Before")} </Text>
                    </th>
                  </tr>
                </thead>
                <tbody>{promotionRows}</tbody>
              </Table>
            </ScrollArea>
          )}

          {activeTab === "incentives" && (
            <ScrollArea h={"400px"} type="auto">
              <Table>
                <thead>
                  <tr>
                    <th>
                      <Text size={headerSize}>{t("Date Range")} </Text>
                    </th>
                    <th>
                      <Text size={headerSize}>{t("Insurer")} </Text>
                    </th>
                    <th>
                      <Text size={headerSize}>{t("Title")}</Text>
                    </th>
                    <th>
                      <Text size={headerSize}>{t("In Force Before")} </Text>
                    </th>
                  </tr>
                </thead>
                <tbody>{incentiveRows}</tbody>
              </Table>
            </ScrollArea>
          )}

          {activeTab === "pendingRenewals" && (
            <>
              <Group mb="xs" spacing="xs" ml={10}>
                <Button
                  loading={downloadingRenewals}
                  onClick={downloadPendingRenewals}
                  variant="light"
                  color="blue"
                  size="xs"
                  leftIcon={<IconDownload size={14} />}
                >
                  {t("Download Report")}
                </Button>
                <Text size="xs" color="dimmed">
                  {pendingRenewals.length}{" "}
                  {t(pendingRenewals.length === 1 ? "record" : "records")}
                </Text>
              </Group>
              <ScrollArea h={"400px"} type="auto">
                <Table>
                  <thead>
                    <tr>
                      <th>
                        <Text size={headerSize}>{t("DN")}</Text>
                      </th>
                      <th>
                        <Text size={headerSize}>{t("Renewal")}</Text>
                      </th>
                      <th>
                        <Text size={headerSize}>{t("Expiry")}</Text>
                      </th>
                      <th>
                        <Text size={headerSize}>{t("Product")}</Text>
                      </th>
                      <th>
                        <Text size={headerSize}>{t("Policy")}</Text>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...pendingRenewals]
                      .sort((a, b) => {
                        const dateA = a.period?.end
                          ? moment(a.period.end).valueOf()
                          : 0;
                        const dateB = b.period?.end
                          ? moment(b.period.end).valueOf()
                          : 0;
                        return dateA - dateB;
                      })
                      .map((dn) => (
                        <tr key={dn._id} style={{ verticalAlign: "top" }}>
                          <td>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "2px",
                              }}
                            >
                              <Group noWrap spacing={1}>
                                <Badge
                                  color={
                                    dn.type === "NEW"
                                      ? "green"
                                      : dn.type === "RENEW"
                                        ? "blue"
                                        : "gray"
                                  }
                                  size="xs"
                                  radius={0}
                                >
                                  {dn.type === "NEW"
                                    ? "N"
                                    : dn.type === "RENEW"
                                      ? "R"
                                      : "N"}
                                </Badge>
                                <Badge
                                  color={
                                    dn.status === "CONFIRMED" ||
                                    dn.status === "SETTLED"
                                      ? "blue"
                                      : "yellow"
                                  }
                                  size="xs"
                                  radius={0}
                                >
                                  {dn.status}
                                </Badge>
                              </Group>
                              {canOpenEntity("debitNote") ? (
                                <Text
                                  component="span"
                                  size={txtSize}
                                  style={{
                                    lineHeight: 1.2,
                                    color: "#c1c2c5",
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    openLinkedEntity("debitNote", dn._id, dn.code)
                                  }
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.color = "#228be6";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.color = "#c1c2c5";
                                  }}
                                >
                                  {dn.code}
                                </Text>
                              ) : (
                                <Text
                                  size={txtSize}
                                  style={{ lineHeight: 1.2 }}
                                >
                                  {dn.code}
                                </Text>
                              )}
                              <Text
                                size="xs"
                                color="dimmed"
                                style={{ lineHeight: 1.2 }}
                              >
                                {dn.date
                                  ? moment(dn.date).format("YYYY-MM-DD")
                                  : ""}
                              </Text>
                            </div>
                          </td>
                          <td>
                            {dn.renewal ? (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "2px",
                                }}
                              >
                                <Group noWrap spacing={2}>
                                  <Badge
                                    size="xs"
                                    radius={0}
                                    color={
                                      dn.renewal.status === "CONFIRM"
                                        ? "green"
                                        : dn.renewal.status === "PENDING"
                                          ? "yellow"
                                          : dn.renewal.status === "WAIT"
                                            ? "blue"
                                            : "gray"
                                    }
                                  >
                                    {dn.renewal.status}
                                  </Badge>
                                  <Badge
                                    size="xs"
                                    radius={0}
                                    color={
                                      dn.renewal.receivedRenewal?.date
                                        ? "blue"
                                        : "orange"
                                    }
                                  >
                                    {dn.renewal.receivedRenewal?.date
                                      ? "Received"
                                      : "Not Received"}
                                  </Badge>
                                </Group>
                                {canOpenEntity("renewal") ? (
                                  <Text
                                    component="span"
                                    size={txtSize}
                                    style={{
                                      lineHeight: 1.2,
                                      color: "#c1c2c5",
                                      cursor: "pointer",
                                    }}
                                    onClick={() =>
                                      openLinkedEntity(
                                        "renewal",
                                        dn.renewal._id,
                                        dn.renewal.code,
                                      )
                                    }
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.color = "#228be6";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.color = "#c1c2c5";
                                    }}
                                  >
                                    {dn.renewal.code}
                                  </Text>
                                ) : (
                                  <Text
                                    size={txtSize}
                                    style={{ lineHeight: 1.2 }}
                                  >
                                    {dn.renewal.code}
                                  </Text>
                                )}
                                {dn.renewal.date && (
                                  <Text
                                    size="xs"
                                    color="dimmed"
                                    style={{ lineHeight: 1.2 }}
                                  >
                                    {moment(dn.renewal.date).format(
                                      "YYYY-MM-DD",
                                    )}
                                  </Text>
                                )}
                              </div>
                            ) : (
                              <Text size={txtSize} color="dimmed">
                                -
                              </Text>
                            )}
                          </td>
                          <td>
                            {dn.period?.end && (
                              <Text size={txtSize}>
                                {moment(dn.period.end).format("YYYY/MM/DD")}
                              </Text>
                            )}
                          </td>
                          <td>
                            {dn.product ? (
                              <div>
                                <Text lineClamp={1} size={txtSize}>
                                  {dn.product.name}
                                </Text>
                                <Text
                                  color="dimmed"
                                  lineClamp={1}
                                  size={txtSize}
                                >
                                  {dn.product.cname}
                                </Text>
                                {dn.product.provider && (
                                  <Text
                                    color="dimmed"
                                    lineClamp={1}
                                    size="xs"
                                    italic
                                  >
                                    {dn.product.provider.name}
                                  </Text>
                                )}
                                {!_.isEmpty(dn.particulars) && (
                                  <Text
                                    color="green"
                                    lineClamp={1}
                                    size="xs"
                                    italic
                                  >
                                    {dn.particulars
                                      .filter((p) => p.isKey)
                                      .map((p) => p.detail)
                                      .join("/ ")}
                                  </Text>
                                )}
                              </div>
                            ) : (
                              <div>
                                <Text lineClamp={1} size={txtSize}>
                                  {dn.productType?.name}
                                </Text>
                                <Text
                                  color="dimmed"
                                  lineClamp={1}
                                  size={txtSize}
                                >
                                  {dn.productType?.cname}
                                </Text>
                                {dn.provider && (
                                  <Text
                                    color="dimmed"
                                    lineClamp={1}
                                    size="xs"
                                    italic
                                  >
                                    {dn.provider.name}
                                  </Text>
                                )}
                                {!_.isEmpty(dn.particulars) && (
                                  <Text
                                    color="green"
                                    lineClamp={1}
                                    size="xs"
                                    italic
                                  >
                                    {dn.particulars
                                      .filter((p) => p.isKey)
                                      .map((p) => p.detail)
                                      .join("/ ")}
                                  </Text>
                                )}
                              </div>
                            )}
                          </td>
                          <td>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "2px",
                              }}
                            >
                              <Text size={txtSize} style={{ lineHeight: 1.2 }}>
                                {dn.policyNumber ?? ""}
                              </Text>
                              {dn.client &&
                                (canOpenEntity("client") ? (
                                  <Text
                                    component="span"
                                    size="xs"
                                    lineClamp={2}
                                    style={{
                                      lineHeight: 1.2,
                                      color: "#909296",
                                      cursor: "pointer",
                                    }}
                                    onClick={() =>
                                      openLinkedEntity(
                                        "client",
                                        dn.client._id,
                                        dn.client.name,
                                      )
                                    }
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.color = "#228be6";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.color = "#909296";
                                    }}
                                  >
                                    {dn.client.name}
                                  </Text>
                                ) : (
                                  <Text
                                    size="xs"
                                    lineClamp={2}
                                    style={{ lineHeight: 1.2 }}
                                  >
                                    {dn.client.name}
                                  </Text>
                                ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    {pendingRenewals.length === 0 && (
                      <tr>
                        <td colSpan={5}>
                          <Text size={txtSize} color="dimmed" align="center">
                            {t("No pending renewals")}
                          </Text>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </ScrollArea>
            </>
          )}
        </Box>
        <Box sx={{ flexShrink: 0, pl: "md" }}>
          <EventCalendar events={events} onMonthChange={setCurrentDate} />
        </Box>
      </Group>
    </FormSectionCard>
  );
};

export default EventSection;
